import { useEffect } from 'react';
import { Link } from 'react-router-dom';

// Novidades + "como fazer": o que mudou no aplicativo e o passo a passo das ações do dia a dia.
// Ao publicar uma nova leva de novidades, troque NOVIDADES_VERSAO para o selo "novo" voltar a aparecer no menu.
export const NOVIDADES_VERSAO = '2026-10-29';
const KEY = 'novidades:vista';

export function novidadesPendentes(): boolean {
  try {
    return window.localStorage.getItem(KEY) !== NOVIDADES_VERSAO;
  } catch {
    return false;
  }
}

function marcarVistas() {
  try {
    window.localStorage.setItem(KEY, NOVIDADES_VERSAO);
    window.dispatchEvent(new Event('novidades-vistas'));
  } catch {
    /* sem armazenamento: só não lembra */
  }
}

function Passos({ children }: { children: React.ReactNode }) {
  return <ol className="passos">{children}</ol>;
}

export function NovidadesPage() {
  useEffect(() => marcarVistas(), []);
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Novidades e como fazer</h1>
          <p>O que mudou no aplicativo e o passo a passo das ações do dia a dia. Nada aqui envia mensagem ao cliente.</p>
        </div>
      </div>

      <nav className="card novidades-indice" aria-label="Nesta página">
        <strong>Ir para:</strong>{' '}
        <a href="#o-que-mudou">O que mudou</a> · <a href="#ciclo">O ciclo</a> · <a href="#acao-1">Marcar</a> · <a href="#acao-2">Registrar da agenda</a> ·{' '}
        <a href="#acao-3">Registro rápido</a> · <a href="#acao-4">Banco de horas</a> · <a href="#acao-5">Faturar</a> · <a href="#acao-6">Enviar ao cliente</a> ·{' '}
        <a href="#erros">Quando o app recusa</a>
      </nav>

      <section className="card novidade-destaque" id="o-que-mudou">
        <p className="small muted">29/10/2026</p>
        <h2>O que mudou</h2>
        <ul>
          <li>
            <strong>Menu em 3 grupos:</strong> Dia a dia, Controle e Administração.
          </li>
          <li>
            <strong>Um clique na agenda:</strong> no compromisso, <strong>Foi realizado — registrar atendimento</strong> marca como realizado e já abre o registro
            preenchido (projeto, contrato, modalidade, horário e categoria).
          </li>
          <li>
            <strong>Quadro amarelo “Para confirmar, falta…”</strong> no registro: mostra o que ainda falta e fica verde quando está tudo certo.
          </li>
          <li>
            <strong>Botão único “Salvar e confirmar”:</strong> não precisa mais salvar e depois conferir em dois passos.
          </li>
          <li>
            <strong>Registro rápido mais esperto:</strong> o texto de “O que foi feito” vira a descrição; projeto com um só contrato já vem escolhido; a linha de
            categoria já vem pronta e o tempo dela acompanha a duração.
          </li>
          <li>
            <strong>Quadros “Como funciona”</strong> no alto de Execuções, Bancos de horas e Faturamento (clique no título para fechar).
          </li>
        </ul>
      </section>

      <section className="card" id="ciclo">
        <h2>O ciclo em 4 passos</h2>
        <Passos>
          <li>
            <strong>Marcar</strong> o compromisso na Agenda.
          </li>
          <li>
            <strong>Registrar</strong> o atendimento depois que ele acontece.
          </li>
          <li>
            <strong>Confirmar</strong>: é aqui que as horas saem do banco ou viram cobrança.
          </li>
          <li>
            <strong>Cobrar</strong>: conferir o item no Faturamento e registrar a nota.
          </li>
        </Passos>
      </section>

      <section className="card" id="acao-1">
        <h2>1. Marcar um compromisso</h2>
        <p>
          <strong>Onde:</strong> <Link to="/agenda/novo">Agenda › Novo compromisso</Link>
        </p>
        <Passos>
          <li>Escolha o <strong>Tipo</strong> e escreva o <strong>Título</strong>.</li>
          <li>Escolha o <strong>Projeto</strong> (o cliente vem junto).</li>
          <li>
            Preencha <strong>Data</strong>, <strong>Início</strong> e <strong>Fim</strong>.
          </li>
          <li>
            Escolha <strong>Categoria prevista</strong> e <strong>Módulo/área previsto</strong>: eles passam direto para o registro.
          </li>
          <li>
            <strong>Salvar</strong>.
          </li>
        </Passos>
        <p className="small muted">
          Mudar a data: abra o compromisso › <strong>Reagendar</strong>. O original fica no histórico.
        </p>
      </section>

      <section className="card" id="acao-2">
        <h2>2. Registrar o atendimento que estava na agenda</h2>
        <p>
          <strong>Onde:</strong> <Link to="/agenda">Agenda</Link> › abra o compromisso (depois do horário).
        </p>
        <Passos>
          <li>
            Clique em <strong>Foi realizado — registrar atendimento</strong>. O registro abre preenchido.
          </li>
          <li>
            Escreva a <strong>Descrição do conteúdo</strong> (vai para o relatório do cliente).
          </li>
          <li>
            Quando o quadro ficar verde, clique em <strong>Salvar e confirmar</strong> › <strong>Confirmar execução</strong>.
          </li>
        </Passos>
        <p className="small muted">
          Cliente faltou: <strong>Cliente não compareceu</strong>. Não vai acontecer: <strong>Cancelar compromisso</strong>. Nenhum dos dois consome horas.
        </p>
      </section>

      <section className="card" id="acao-3">
        <h2>3. Registro rápido (sem agenda, até pelo celular)</h2>
        <p>
          <strong>Onde:</strong> <Link to="/execucoes/nova">Nova execução</Link>. Funciona sem internet: o rascunho sobe quando a conexão voltar.
        </p>
        <Passos>
          <li>
            Escolha o <strong>Projeto</strong>, escreva <strong>O que foi feito</strong>, a <strong>Duração</strong> (ex.: 1h30) e a{' '}
            <strong>Modalidade</strong>.
          </li>
          <li>
            <strong>Salvar rascunho</strong> (nada é consumido ainda).
          </li>
          <li>
            <strong>Completar agora</strong>: escolha a <strong>Categoria</strong> e o <strong>Módulo/área</strong> — o resto já vem preenchido.
          </li>
          <li>
            <strong>Salvar e confirmar</strong> › <strong>Confirmar execução</strong>.
          </li>
        </Passos>
        <p className="small muted">
          Projeto de outro sócio: marque <strong>Pesquisar todos os projetos</strong> e confirme o aviso.
        </p>
      </section>

      <section className="card" id="acao-4">
        <h2>4. Banco de horas: saldo, excedente e compra de horas</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Tipo de contrato</th>
              <th>Ao confirmar o atendimento</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Banco de horas</td>
              <td>Desconta do saldo. Nenhuma cobrança nova.</td>
            </tr>
            <tr>
              <td>Hora avulsa</td>
              <td>Cria a cobrança (horas × valor da hora) no Faturamento.</td>
            </tr>
            <tr>
              <td>Mensalidade</td>
              <td>Só registra a produção.</td>
            </tr>
            <tr>
              <td>Valor fechado</td>
              <td>Só registra o esforço.</td>
            </tr>
          </tbody>
        </table>
        <ul>
          <li>
            <strong>Ver saldo:</strong> <Link to="/bancos-de-horas">Bancos de horas</Link> › clique no contrato.
          </li>
          <li>
            <strong>Passou do saldo:</strong> no atendimento aparece “Excedente… aguardando decisão”. Escolha <strong>Deduzir</strong>,{' '}
            <strong>Faturar avulso</strong> ou <strong>Não cobrar</strong>. Nada é cobrado sozinho.
          </li>
          <li>
            <strong>Cliente comprou mais horas:</strong> abra o banco › <strong>Adicionar horas</strong> › horas e valor › <strong>Registrar</strong>.
          </li>
          <li>
            <strong>Lançou errado:</strong> abra o atendimento › <strong>Anular execução</strong> › motivo. As horas voltam como estorno.
          </li>
        </ul>
      </section>

      <section className="card" id="acao-5">
        <h2>5. Faturar</h2>
        <p>
          <strong>Onde:</strong> <Link to="/faturamento">Faturamento</Link>
        </p>
        <Passos>
          <li>
            Aba <strong>Pendente de conferência</strong>: confira › <strong>Conferido — pronto para envio</strong>.
          </li>
          <li>
            Aba <strong>Pronto para envio</strong>: emita a nota › <strong>Registrar faturamento manual</strong> › número da nota ›{' '}
            <strong>Confirmar registro</strong>.
          </li>
          <li>
            O item vai para <strong>Faturado</strong>. Faturado não significa recebido.
          </li>
        </Passos>
      </section>

      <section className="card" id="acao-6">
        <h2>6. Enviar relatório ou extrato ao cliente</h2>
        <Passos>
          <li>
            Abra o atendimento confirmado › <strong>Preparar envio por e-mail</strong> (ou WhatsApp).
          </li>
          <li>
            Confira destinatários, assunto, mensagem e o PDF (<strong>Ver PDF</strong>).
          </li>
          <li>
            Marque <strong>Conferi…</strong> › <strong>Confirmar e enviar</strong>.
          </li>
        </Passos>
        <p className="small muted">
          Extrato do banco: Bancos de horas › contrato › <strong>Extrato para o cliente</strong>. Lembrete de reunião: no compromisso, <strong>Preparar lembrete</strong>.
        </p>
      </section>

      <section className="card" id="erros">
        <h2>Quando o app recusa</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Mensagem</th>
              <th>O que fazer</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>“Falta preencher: categoria…”</td>
              <td>Escolha Categoria e Módulo/área na linha do rateio.</td>
            </tr>
            <tr>
              <td>“Tempo da categoria igual à duração”</td>
              <td>Com 2 ou mais categorias, a soma dos tempos tem que dar a duração.</td>
            </tr>
            <tr>
              <td>“Descrição do conteúdo”</td>
              <td>Escreva o que foi feito no campo Descrição do conteúdo.</td>
            </tr>
            <tr>
              <td>“Este projeto está vinculado a outro consultor”</td>
              <td>É só um aviso: clique OK e siga.</td>
            </tr>
            <tr>
              <td>“Nenhum banco de horas vigente… tem saldo”</td>
              <td>Bancos de horas › Adicionar horas, depois volte e deduza.</td>
            </tr>
          </tbody>
        </table>
      </section>
    </>
  );
}
